<script setup lang="ts">
import { computed, ref } from 'vue';
import {
  createResourceFilter,
  removeResourceFilter,
  updateResourceFilter,
} from '/@stores/resources';
import { ResourceFilter } from '/@types/resources';
import { Valuelist } from '/@types/valuelist';
import { toast } from '/@utilities/toast';

const props = defineProps<{
  initialFilter?: ResourceFilter;
  isPersonal?: boolean;
  tagValueList: Valuelist | null;
  filterNames: Set<string>;
}>();

const emit = defineEmits<{
  created: [filter: ResourceFilter];
  updated: [filter: ResourceFilter];
  removed: [];
  close: [];
}>();

const isEdit = computed(() => props.initialFilter != null);
const form = ref({
  name: props.initialFilter?.name ?? (props.isPersonal ? 'Ny personlig gruppe' : 'Ny firmagruppe'),
  type: props.initialFilter?.type ?? '',
});
const submitting = ref(false);
const error = ref(false);

const isFormNameValid = computed(() => {
  return !(props.filterNames.has(form.value.name) && form.value.name !== props.initialFilter?.name);
});

function create() {
  submitting.value = true;
  error.value = false;
  createResourceFilter(form.value.name, form.value.type, {
    isUserSpecific: props.isPersonal ?? false,
  })
    .then((res) => {
      if (!res) return;
      toast(`Opprettet ny gruppe «${form.value.name}»`);
      emit('created', res);
      emit('close');
    })
    .catch(() => {
      error.value = true;
    })
    .finally(() => {
      submitting.value = false;
    });
}

function update() {
  submitting.value = true;
  error.value = false;
  updateResourceFilter(props.initialFilter!.id, { name: form.value.name, type: form.value.type })
    .then(() => {
      emit('updated', { ...props.initialFilter!, name: form.value.name, type: form.value.type });
      emit('close');
    })
    .catch(() => {
      error.value = true;
    })
    .finally(() => {
      submitting.value = false;
    });
}

function handleRemove() {
  removeResourceFilter(props.initialFilter!.id)
    .then(() => {
      emit('removed');
      emit('close');
    })
    .catch(() => {
      toast('Kunne ikke slette gruppe');
    });
}
</script>

<template>
  <df-modal>
    <form class="dialog" v-on:submit.prevent="isEdit ? update() : create()">
      <header class="head">
        <df-button v-on:click="emit('close')">
          <template v-slot:icon>
            <df-icon code="f060" />
          </template>
        </df-button>

        <h1 class="title-md">
          {{ isEdit ? 'Rediger' : 'Ny' }} {{ isPersonal ? 'personlig gruppe' : 'firmagruppe' }}
        </h1>

        <df-delete-button v-if="isEdit" v-on:confirm="handleRemove" />
      </header>

      <df-input type="text" v-model="form.name" label="Navn" elevate no-delay />
      <df-select v-if="tagValueList" label="Type" elevate>
        <template v-slot:text>{{ form.type || 'Velg type' }}</template>

        <df-dropdown2-item v-on:click="form.type = ''">Ingen</df-dropdown2-item>

        <df-dropdown2-item
          v-for="[id, item] in tagValueList.valuelistItems"
          :key="id"
          v-on:click="form.type = item.name"
        >
          {{ item.name }}
        </df-dropdown2-item>
      </df-select>

      <div class="loading-icon" v-if="submitting"></div>
      <div class="danger-text" v-if="!form.name">Navn er påkrevd</div>
      <div class="danger-text" v-if="!isFormNameValid">Navnet er allerede i bruk</div>
      <div class="danger-text" v-if="error">
        {{ isEdit ? 'Kunne ikke oppdatere gruppe' : 'Kunne ikke opprette gruppe' }}
      </div>

      <df-button
        primary
        elevate
        type="submit"
        :disabled="submitting || !form.name || !isFormNameValid"
        >Lagre</df-button
      >
    </form>
  </df-modal>
</template>

<style scoped>
.danger-text {
  color: var(--color-danger_text);
}
</style>
